import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormStepProps, ApplicationFormData } from '../../types/application';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
    gap: 30px;
  }
`;

const MainForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  
  &.required::after {
    content: ' *';
    color: #d32f2f;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
`;

const ErrorMessage = styled.span`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;

const NameGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const AddressGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr;
  }
  
  .full-width {
    grid-column: 1 / -1;
  }
`;


const SidebarTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  font-size: 1.1rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
    padding-left: 16px;
    position: relative;
    
    &::before {
      content: '•';
      color: ${props => props.theme.primaryColor};
      position: absolute;
      left: 0;
    }
  }
`;

const countries = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }
];


const ApplicantInformation: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange
}) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      applicantFirstName: data.applicantFirstName,
      applicantLastName: data.applicantLastName,
      dateOfBirth: data.dateOfBirth,
      residentialAddress: data.residentialAddress
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    updateData(watchedValues as Partial<ApplicationFormData>);
  }, [watchedValues, updateData]);

  useEffect(() => {
    // Simple validation - check required fields
    const hasRequiredFields = watchedValues.applicantFirstName && 
                             watchedValues.applicantLastName && 
                             watchedValues.dateOfBirth;
    onValidationChange(!!hasRequiredFields);
  }, [watchedValues, onValidationChange]);

  return (
    <StepContainer>
      <StepTitle>Applicant Information</StepTitle>
      <StepDescription>
        The person filing this registration form must be a Director or Shareholder of the business. 
        All information will be verified against official identification documents.
      </StepDescription>

      <FormGrid>
        <MainForm>
          <FormGroup>
            <Label className="required">Applicant Name</Label>
            <NameGrid>
              <div>
                <Input
                  {...register('applicantFirstName')}
                  placeholder="First Name"
                  hasError={!!errors.applicantFirstName}
                />
                {errors.applicantFirstName && <ErrorMessage>{errors.applicantFirstName.message}</ErrorMessage>}
              </div>
              <div>
                <Input
                  {...register('applicantLastName')}
                  placeholder="Last Name"
                  hasError={!!errors.applicantLastName}
                />
                {errors.applicantLastName && <ErrorMessage>{errors.applicantLastName.message}</ErrorMessage>}
              </div>
            </NameGrid>
          </FormGroup>

          <FormGroup>
            <Label className="required">Your Date of Birth</Label>
            <Input
              type="date"
              {...register('dateOfBirth')}
              hasError={!!errors.dateOfBirth}
            />
            {errors.dateOfBirth && <ErrorMessage>{errors.dateOfBirth.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Your Residential Address</Label>
            <AddressGrid>
              <div className="full-width">
                <Input
                  {...register('residentialAddress.street')}
                  placeholder="Street address"
                  hasError={!!errors.residentialAddress?.street}
                />
                {errors.residentialAddress?.street && <ErrorMessage>{errors.residentialAddress.street.message}</ErrorMessage>}
              </div>
              
              <div>
                <Input
                  {...register('residentialAddress.city')}
                  placeholder="City"
                  hasError={!!errors.residentialAddress?.city}
                />
                {errors.residentialAddress?.city && <ErrorMessage>{errors.residentialAddress.city.message}</ErrorMessage>}
              </div>
              
              <div>
                <Input
                  {...register('residentialAddress.state')}
                  placeholder="State/Province (optional)"
                />
              </div>
              
              <div>
                <Input
                  {...register('residentialAddress.postalCode')}
                  placeholder="Postal Code"
                  hasError={!!errors.residentialAddress?.postalCode}
                />
                {errors.residentialAddress?.postalCode && <ErrorMessage>{errors.residentialAddress.postalCode.message}</ErrorMessage>}
              </div>
              
              <div>
                <Select
                  {...register('residentialAddress.country')}
                  hasError={!!errors.residentialAddress?.country}
                >
                  <option value="">Choose country</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
                {errors.residentialAddress?.country && <ErrorMessage>{errors.residentialAddress.country.message}</ErrorMessage>}
              </div>
            </AddressGrid>
          </FormGroup>
        </MainForm>

        <Sidebar>
          <SidebarTitle>Requirements</SidebarTitle>
          <InfoList>
            <li>Must be a Director or Shareholder</li>
            <li>Legal authority to register on behalf of company</li>
            <li>Current government-issued photo ID required</li>
            <li>Address proof within last 3 months</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Document Verification</SidebarTitle>
          <InfoList>
            <li>Passport or national ID required</li>
            <li>Utility bill or bank statement for address</li>
            <li>All documents must be in full color</li>
            <li>Global ID recognition supported</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Privacy & Security</SidebarTitle>
          <InfoList>
            <li>All personal data encrypted</li>
            <li>GDPR and CCPA compliant</li>
            <li>Secure document upload system</li>
            <li>Data residency compliance</li>
          </InfoList>
        </Sidebar>
      </FormGrid>
    </StepContainer>
  );
};

export default ApplicantInformation;